// ProjectPage — the three-column doc reader wired to live data (RFC-0010 §1/§6/§7,
// PRD-0001 §1.3). Replaces the mock-data doc-reader.tsx.
//
// Columns: doc tree (left) · rendered content (center) · metadata rail (right).
// Data comes from GET /api/projects/:repo (nav/metadata/links/deps, via
// useProject) + GET /api/projects/:repo/docs?path= (the selected doc body, via
// useProjectDoc), composed by useProjectData. The center column renders the
// hardened Phase-2 Markdown pipeline with LinkResolver-mapped links/images,
// DocNavigator breadcrumbs + prev/next, and a "View on GitHub" affordance; the
// right rail carries the TableOfContents, manifest links, and dependency edges.
//
// Phase 4 (§8/§9) layers the state surfaces on top: the ManifestBrokenBanner
// (non-destructive, above the columns), the first-run/empty predicates
// (sync-in-progress / no-manifest), the DocBadges pills (frontmatter type/status)
// and the docOwner rail entry. The dedicated /broken and /archived state pages
// live in project-broken.tsx / project-archived.tsx.

import { useEffect, useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { NavItem } from '@portal/shared-types';
import {
  ArrowClockwise,
  ArrowLeft,
  ArrowRight,
  CaretRight,
  FileText,
  GithubLogo,
  ListBullets,
} from '@phosphor-icons/react';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorPanel } from '@/components/ui/error-panel';
import { Button } from '@/components/ui/button';
import { Drawer } from '@/components/ui/drawer';
import { Skeleton } from '@/components/ui/query-skeletons';
import { MarkdownRenderer } from '@/components/doc-reader/markdown-renderer';
import { DocNavTree } from '@/components/doc-reader/doc-nav-tree';
import { DocNavDrawerProvider, useDocNavDrawer } from '@/context/doc-nav-drawer';
import { useViewportBreakpoint } from '@/hooks/use-viewport-breakpoint';
import { TableOfContents } from '@/components/doc-reader/toc';
import { DocBadges } from '@/components/doc-reader/doc-badges';
import { MetadataLinksList } from '@/components/project/metadata-links-list';
import { ManifestBrokenBanner } from '@/components/project/manifest-broken-banner';
import { useProjectData } from '@/hooks/use-project-data';
import { ApiError } from '@/lib/api-client';
import {
  resolveHref,
  resolveImageSrc,
  type RepoLinkContext,
} from '@/lib/link-resolver';
import { getPrevNext, getBreadcrumbs } from '@/lib/doc-navigator';

/** Stable empty-nav reference so `project?.nav ?? EMPTY_NAV` keeps a fixed
 * identity across renders (avoids re-running the indexedPaths memo). */
const EMPTY_NAV: NavItem[] = [];

function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

function docHref(repo: string, docPath: string): string {
  return `/projects/${encodeURIComponent(repo)}/docs/${docPath}`;
}

export function ProjectPage() {
  const params = useParams();
  const repo = params.repo;
  // `/projects/:repo/docs/*` → the splat is the doc path; `/projects/:repo` → none.
  const splat = params['*'];
  const requestedPath = splat && splat.length > 0 ? splat : undefined;

  const {
    project,
    doc,
    effectivePath,
    isProjectLoading,
    isProjectError,
    projectError,
    isDocLoading,
    isDocError,
    docError,
    refetch,
  } = useProjectData(repo, requestedPath);

  const contentRef = useRef<HTMLDivElement>(null);

  const nav = project?.nav ?? EMPTY_NAV;

  const indexedPaths = useMemo(
    () =>
      new Set(
        nav.filter((n) => n.docPath !== null).map((n) => n.docPath as string),
      ),
    [nav],
  );

  const linkContext: RepoLinkContext | null = project
    ? { repo: project.name, fullName: project.fullName, defaultBranch: project.defaultBranch }
    : null;

  // ── Loading / error / not-found for the project itself ────────────────────
  if (isProjectLoading) {
    return <ProjectPageSkeleton />;
  }

  if (isProjectError || !project || !repo) {
    if (isNotFound(projectError)) {
      return (
        <div className="rounded-xl border border-border bg-surface shadow-[var(--c-shadow-sm)]">
          <EmptyState
            icon={<FileText size={22} />}
            title="Project not found"
            description="This repository isn't connected to the portal, or hasn't been ingested yet."
            action={
              <Link to="/repositories">
                <Button size="md">Back to repositories</Button>
              </Link>
            }
          />
        </div>
      );
    }
    return <ErrorPanel error={projectError} onRetry={() => refetch()} />;
  }

  // ── First-run / empty states (RFC-0010 §9, Task 4.3) ──────────────────────
  // Distinct predicates over the already-loaded project payload (N2 — no second
  // request; `nav.length === 0` is the zero-doc predicate).
  //
  // (1) Sync in progress: the repo is connected but has never completed a sync.
  if (project.lastSyncedAt == null) {
    return (
      <div className="rounded-xl border border-border bg-surface shadow-[var(--c-shadow-sm)]">
        <EmptyState
          icon={<ArrowClockwise size={22} />}
          title="Sync in progress"
          description="This repository is connected but hasn't finished its first sync yet. Check back shortly."
        />
      </div>
    );
  }

  // (2) No manifest anywhere, nothing indexed: synced, auto-index active, but no
  //     docs were discovered. Point the user at the manifest contract.
  if (project.autoIndexed && nav.length === 0 && !project.manifestBroken) {
    return (
      <div className="rounded-xl border border-border bg-surface shadow-[var(--c-shadow-sm)]">
        <EmptyState
          icon={<FileText size={22} />}
          title="No documentation found"
          description="This repository has no manifest and no README or docs files were discovered. Add a .nmblr.portal.yaml to control indexing."
          action={
            <a
              href={`https://github.com/${project.fullName}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="md" variant="secondary">
                <GithubLogo size={15} />
                Open repository
              </Button>
            </a>
          }
        />
      </div>
    );
  }

  // ── Reader affordances derived from the flat nav + selected doc ───────────
  const prevNext = effectivePath ? getPrevNext(nav, effectivePath) : { prev: null, next: null };
  const breadcrumbs = effectivePath
    ? getBreadcrumbs(nav, effectivePath, project.name)
    : [{ label: project.name, docPath: null }];
  const editUrl =
    effectivePath &&
    `https://github.com/${project.fullName}/blob/${project.defaultBranch}/${effectivePath}`;

  return (
    <DocNavDrawerProvider>
      {/* Broken-manifest banner (ADR-0005 / §8) — non-destructive; last-good
          content still renders in the columns below (Task 4.1). */}
      {project.manifestBroken && (
        <ManifestBrokenBanner
          repo={project.name}
          manifestBrokenAt={project.manifestBrokenAt}
          lastSyncedSha={project.lastSyncedSha}
        />
      )}

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[220px_minmax(0,1fr)_220px]">
      {/* ── Left: doc tree ─────────────────────────────────────────────────
          Inline only at lg+ (the reader's actual multi-column point). Below lg
          the tree lives in the page-scoped "Contents" drawer (Task 3.1). */}
      <aside className="hidden lg:sticky lg:top-0 lg:block lg:self-start">
        <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">
          {project.name}
        </p>
        {nav.length > 0 ? (
          <DocNavTree nav={nav} repo={project.name} currentDocPath={effectivePath} />
        ) : (
          <p className="px-3 text-[13px] text-muted">No documents yet.</p>
        )}
      </aside>

      {/* ── Center: rendered document ──────────────────────────────────── */}
      <article className="min-w-0">
        {/* "Contents" trigger — below lg only; opens the doc-nav drawer. */}
        {nav.length > 0 && <ContentsTrigger />}

        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-1.5 text-xs text-muted">
          {breadcrumbs.map((crumb, i) => {
            const isLast = i === breadcrumbs.length - 1;
            const to =
              i === 0
                ? `/projects/${encodeURIComponent(project.name)}`
                : crumb.docPath
                  ? docHref(project.name, crumb.docPath)
                  : null;
            return (
              <span key={`${crumb.label}-${i}`} className="flex items-center gap-1.5">
                {i > 0 && <CaretRight size={11} weight="bold" className="text-text/30" />}
                {isLast || !to ? (
                  <span className={isLast ? 'text-text' : 'text-text-2'}>{crumb.label}</span>
                ) : (
                  <Link to={to} className="transition-colors hover:text-text">
                    {crumb.label}
                  </Link>
                )}
              </span>
            );
          })}
        </nav>

        <ProjectContent
          effectivePath={effectivePath}
          isDocLoading={isDocLoading}
          isDocError={isDocError}
          docError={docError}
          doc={doc}
          contentRef={contentRef}
          onRetry={() => refetch()}
          resolveHref={(href) => {
            if (!linkContext || !effectivePath) return null;
            const r = resolveHref(href, effectivePath, indexedPaths, linkContext);
            return r.href ? { href: r.href, internal: r.kind === 'internal' } : null;
          }}
          resolveImageSrc={(src) =>
            linkContext && effectivePath
              ? resolveImageSrc(src, effectivePath, linkContext)
              : null
          }
        />

        {/* Prev / next */}
        {(prevNext.prev || prevNext.next) && (
          <div className="mt-10 flex items-center justify-between gap-4 border-t border-border pt-6">
            {prevNext.prev?.docPath ? (
              <Link
                to={docHref(project.name, prevNext.prev.docPath)}
                className="group flex items-center gap-2 text-[13px] text-text/60 transition-colors hover:text-text"
              >
                <ArrowLeft size={15} weight="bold" />
                <span className="truncate">{prevNext.prev.title}</span>
              </Link>
            ) : (
              <span />
            )}
            {prevNext.next?.docPath ? (
              <Link
                to={docHref(project.name, prevNext.next.docPath)}
                className="group flex items-center gap-2 text-right text-[13px] text-text/60 transition-colors hover:text-text"
              >
                <span className="truncate">{prevNext.next.title}</span>
                <ArrowRight size={15} weight="bold" />
              </Link>
            ) : (
              <span />
            )}
          </div>
        )}
      </article>

      {/* ── Right: metadata rail ───────────────────────────────────────── */}
      <aside className="flex flex-col gap-6 lg:sticky lg:top-0 lg:self-start">
        {editUrl && (
          <a
            href={editUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-[13px] font-medium text-text/70 transition-colors hover:bg-overlay/5 hover:text-text"
          >
            <GithubLogo size={15} className="shrink-0" />
            View on GitHub
          </a>
        )}

        {/* Doc owner from frontmatter (§5 — surfaced in the rail, not a badge). */}
        {doc?.docOwner && (
          <div>
            <p className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">
              Owner
            </p>
            <p className="px-2 text-[13px] text-text/70">{doc.docOwner}</p>
          </div>
        )}

        <TableOfContents containerRef={contentRef} contentKey={effectivePath} />

        {project.links.length > 0 && (
          <div>
            <p className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">
              Links
            </p>
            <MetadataLinksList links={project.links} />
          </div>
        )}

        {project.replaces.length > 0 && (
          <div>
            <p className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">
              Successors
            </p>
            <ul className="flex flex-col gap-1 px-2 text-[13px] text-text/70">
              {project.replaces.map((r) => (
                <li key={r.targetRepoName}>
                  <Link
                    to={`/projects/${encodeURIComponent(r.targetRepoName)}`}
                    className="transition-colors hover:text-text"
                  >
                    {r.targetRepoName}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {project.dependencies.declared.length > 0 && (
          <div>
            <p className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">
              Depends on
            </p>
            <ul className="flex flex-col gap-1 px-2 text-[13px] text-text/70">
              {project.dependencies.declared.map((d) => (
                <li key={d.targetRepoName}>
                  {d.dangling ? (
                    <span className="text-muted line-through" title="Not connected to the portal">
                      {d.targetRepoName}
                    </span>
                  ) : (
                    <Link
                      to={`/projects/${encodeURIComponent(d.targetRepoName)}`}
                      className="transition-colors hover:text-text"
                    >
                      {d.targetRepoName}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </aside>
      </div>

      {/* Page-scoped "Contents" drawer — reuses the Phase 1 Drawer primitive via a
          SEPARATE provider; mounted (with its scrim/focus-trap/scroll-lock) only
          below lg, where the inline tree is hidden (Task 3.1). */}
      {nav.length > 0 && (
        <ReaderDrawer nav={nav} repo={project.name} currentDocPath={effectivePath} />
      )}
    </DocNavDrawerProvider>
  );
}

// ── Contents drawer trigger + drawer (Task 3.1) ──────────────────────────────

/**
 * "Contents" button shown only below `lg` (CSS `lg:hidden`); opens the doc-nav
 * drawer. At lg+ the inline left aside carries the tree, so the trigger is hidden.
 */
function ContentsTrigger() {
  const { open } = useDocNavDrawer();
  return (
    <button
      type="button"
      onClick={open}
      aria-haspopup="dialog"
      className="mb-5 inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-border px-3 py-2 text-[13px] font-medium text-text/70 transition-colors hover:bg-overlay/5 hover:text-text lg:hidden"
    >
      <ListBullets size={16} className="shrink-0" aria-hidden="true" />
      Contents
    </button>
  );
}

/**
 * The reader's off-canvas "Contents" drawer. JS-gated on `isBelowLg`: at lg+ no
 * drawer machinery (scrim, focus-trap, body scroll-lock) mounts at all — the
 * inline aside handles navigation. Resizing up to lg+ while open auto-closes it
 * so no stale open state lingers (mirrors PortalLayout's ShellDrawer).
 */
function ReaderDrawer({
  nav,
  repo,
  currentDocPath,
}: {
  nav: NavItem[];
  repo: string;
  currentDocPath: string | undefined;
}) {
  const { isBelowLg } = useViewportBreakpoint();
  const { isOpen, close } = useDocNavDrawer();

  useEffect(() => {
    if (!isBelowLg && isOpen) close();
  }, [isBelowLg, isOpen, close]);

  if (!isBelowLg) return null;

  return (
    <Drawer isOpen={isOpen} onClose={close} label="Contents" side="left">
      <div className="p-4">
        <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">
          {repo}
        </p>
        <DocNavTree nav={nav} repo={repo} currentDocPath={currentDocPath} />
      </div>
    </Drawer>
  );
}

// ── Center-column content branch (loading / error / not-found / rendered) ────

interface ProjectContentProps {
  effectivePath: string | undefined;
  isDocLoading: boolean;
  isDocError: boolean;
  docError: unknown;
  doc: ReturnType<typeof useProjectData>['doc'];
  contentRef: React.RefObject<HTMLDivElement | null>;
  onRetry: () => void;
  resolveHref: (href: string) => { href: string; internal: boolean } | null;
  resolveImageSrc: (src: string) => string | null;
}

function ProjectContent({
  effectivePath,
  isDocLoading,
  isDocError,
  docError,
  doc,
  contentRef,
  onRetry,
  resolveHref: resolveHrefFn,
  resolveImageSrc: resolveImageSrcFn,
}: ProjectContentProps) {
  if (!effectivePath) {
    return (
      <div className="rounded-xl border border-border bg-surface shadow-[var(--c-shadow-sm)]">
        <EmptyState
          icon={<FileText size={22} />}
          title="No documentation found"
          description="This repository has no indexed documents yet."
        />
      </div>
    );
  }

  if (isDocLoading) {
    return (
      <div role="status" aria-label="Loading document" className="flex flex-col gap-3">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }

  if (isDocError || !doc) {
    if (isNotFound(docError)) {
      return (
        <div className="rounded-xl border border-border bg-surface shadow-[var(--c-shadow-sm)]">
          <EmptyState
            icon={<FileText size={22} />}
            title="Document not found"
            description="This document doesn't exist in the repository, or hasn't been ingested yet."
          />
        </div>
      );
    }
    return <ErrorPanel error={docError} onRetry={onRetry} />;
  }

  return (
    <>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-border pb-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-mono text-[11px] text-muted">{doc.path}</span>
          {/* Frontmatter type/status pills adjacent to the doc (§5, Task 4.4). */}
          <DocBadges docType={doc.docType} docStatus={doc.docStatus} />
        </div>
        {doc.syncedAt && (
          <span className="text-[11px] text-muted">
            Updated {new Date(doc.syncedAt).toLocaleDateString()}
          </span>
        )}
      </div>
      <div ref={contentRef}>
        <MarkdownRenderer
          content={doc.body}
          resolveHref={resolveHrefFn}
          resolveImageSrc={resolveImageSrcFn}
        />
      </div>
    </>
  );
}

// ── Loading skeleton mirroring the three-column layout ───────────────────────

function ProjectPageSkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading project"
      className="grid grid-cols-1 gap-8 lg:grid-cols-[220px_minmax(0,1fr)_220px]"
    >
      <div className="flex flex-col gap-2">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-5/6" />
      </div>
      <div className="flex flex-col gap-3">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-2/3" />
      </div>
      <div className="flex flex-col gap-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-4 w-5/6" />
      </div>
    </div>
  );
}
